import { useEffect, useState, useCallback } from "react";
import { Download, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["Yeni", "Okundu", "Görüşülüyor", "Anlaşıldı", "Reddedildi"];
const STATUS_COLORS = { Yeni: "#C1121F", Okundu: "#A3A3A3", "Görüşülüyor": "#B8860B", "Anlaşıldı": "#3E5C3A", Reddedildi: "#525252" };

export default function Submissions() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get("/admin/submissions", { params: { search, status } }).then((r) => setItems(r.data)).catch(() => {});
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (sub) => {
    setSelected(sub);
    setNote(sub.admin_note || "");
    if (sub.status === "Yeni") {
      const { data } = await api.patch(`/admin/submissions/${sub.id}`, { status: "Okundu" });
      setItems((list) => list.map((s) => (s.id === sub.id ? data : s)));
      setSelected(data);
    }
  };

  const updateStatus = async (newStatus) => {
    const { data } = await api.patch(`/admin/submissions/${selected.id}`, { status: newStatus });
    setSelected(data);
    setItems((list) => list.map((s) => (s.id === data.id ? data : s)));
    toast.success("Durum güncellendi");
  };

  const saveNote = async () => {
    const { data } = await api.patch(`/admin/submissions/${selected.id}`, { admin_note: note });
    setSelected(data);
    setItems((list) => list.map((s) => (s.id === data.id ? data : s)));
    toast.success("Not kaydedildi");
  };

  const remove = async (id) => {
    if (!window.confirm("Bu başvuru silinsin mi?")) return;
    await api.delete(`/admin/submissions/${id}`);
    setItems((list) => list.filter((s) => s.id !== id));
    setSelected(null);
    toast.success("Başvuru silindi");
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/submissions-export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "basvurular.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Form Başvuruları</h1>
        <button data-testid="export-csv-btn" onClick={exportCsv} className="flex items-center gap-2 border border-[#262626] bg-[#141414] px-4 py-2 text-sm hover:border-[#8B0000] transition-colors">
          <Download size={15} /> CSV İndir
        </button>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input data-testid="submissions-search" className="field-dark pl-10" placeholder="Firma, isim, e-posta ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select data-testid="submissions-status-filter" className="field-dark w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tüm Durumlar</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="border border-[#232323] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#141414] text-left text-xs tracking-wider text-[#525252] uppercase">
              <th className="p-3">Tarih</th><th className="p-3">Firma</th><th className="p-3">Yetkili</th><th className="p-3">Kampanya</th><th className="p-3">Durum</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`submission-row-${s.id}`} onClick={() => openDetail(s)} className="border-t border-[#1f1f1f] hover:bg-[#141414] cursor-pointer transition-colors">
                <td className="p-3 text-[#A3A3A3] whitespace-nowrap">{new Date(s.created_at).toLocaleDateString("tr-TR")}</td>
                <td className="p-3 font-semibold">{s.company}</td>
                <td className="p-3 text-[#A3A3A3]">{s.name}</td>
                <td className="p-3 text-[#A3A3A3]">{s.campaign_type || "—"}</td>
                <td className="p-3"><span className="px-2 py-1 text-xs border" style={{ color: STATUS_COLORS[s.status], borderColor: STATUS_COLORS[s.status] + "66" }}>{s.status}</span></td>
                <td className="p-3">
                  <button data-testid={`delete-submission-${s.id}`} onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-[#525252] hover:text-[#C1121F]"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#525252]">Başvuru bulunamadı.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111111] border-[#262626] text-[#EDEDEA] max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selected.company}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {[["Yetkili", selected.name], ["E-posta", selected.email], ["Telefon", selected.phone || "—"], ["Kampanya", selected.campaign_type || "—"], ["Bütçe", selected.budget || "—"], ["Hedef Tarih", selected.target_date || "—"]].map(([k, v]) => (
                  <div key={k}><p className="text-xs text-[#525252] uppercase tracking-wider">{k}</p><p className="mt-1">{v}</p></div>
                ))}
              </div>
              <div>
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">Mesaj</p>
                <p className="text-sm text-[#A3A3A3] whitespace-pre-wrap border border-[#1f1f1f] bg-[#0D0D0D] p-4">{selected.message}</p>
              </div>
              <div>
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-2">Durum</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} data-testid={`status-btn-${s}`} onClick={() => updateStatus(s)}
                      className={`px-3 py-1.5 text-xs border transition-colors ${selected.status === s ? "bg-[#8B0000] border-[#8B0000] text-white" : "border-[#262626] text-[#A3A3A3] hover:border-[#8B0000]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-2">Admin Notu</p>
                <textarea data-testid="admin-note-input" className="field-dark min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} />
                <button data-testid="save-note-btn" onClick={saveNote} className="mt-2 bg-[#8B0000] hover:bg-[#C1121F] px-4 py-2 text-xs tracking-wider uppercase transition-colors">Notu Kaydet</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
